import React, { useEffect, useRef, useState } from 'react';
import { Appointment, Customer, QuestionnaireTemplate, Service, SystemConfig } from '@/types';
import { useCustomerMutations } from '@features/admin/data';
import { Calendar, Edit2, Key, Mail, Phone } from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { formatDobForDisplay } from './CustomerEditor';
import { CustomerHistoryPanel } from './CustomerHistoryPanel';
import { CustomerMedicalReview } from './CustomerMedicalReview';
import { CustomerDispatchLogs } from './CustomerDispatchLogs';

interface CustomerDetailPanelProps {
  customer: Customer;
  services: Service[];
  questionnaires: QuestionnaireTemplate[];
  systemConfig?: SystemConfig;
  canEdit: boolean;
  canRevokeCertificate?: boolean;
  canOverrideLockedPaperwork?: boolean;
  onEdit: () => void;
  onClose: () => void;
}

export const CustomerDetailPanel: React.FC<CustomerDetailPanelProps> = ({
  customer,
  services,
  questionnaires,
  systemConfig,
  canEdit,
  canRevokeCertificate = false,
  canOverrideLockedPaperwork = false,
  onEdit,
  onClose
}) => {
  const customerMutations = useCustomerMutations();
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const reviewPanelRef = useRef<HTMLDivElement>(null);
  const [isReviewHighlighted, setIsReviewHighlighted] = useState(false);
  const highlightTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(highlightTimeoutRef.current), []);

  // Clicking Review must always produce visible feedback — with a single
  // booking the card is auto-selected, so selection alone changes nothing.
  // Below the xl breakpoint the three columns stack (panel sits under the
  // history list); at xl+ they are side by side, so only nudge into view.
  const openReview = () => {
    const stacked = !window.matchMedia('(min-width: 1280px)').matches;
    const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    reviewPanelRef.current?.scrollIntoView({ behavior, block: stacked ? 'start' : 'nearest' });
    setIsReviewHighlighted(false);
    window.clearTimeout(highlightTimeoutRef.current);
    requestAnimationFrame(() => {
      setIsReviewHighlighted(true);
      highlightTimeoutRef.current = window.setTimeout(() => setIsReviewHighlighted(false), 1600);
    });
  };

  const handleResetPassword = async () => {
    const confirmed = await showConfirmModal(`Send a password reset email to ${customer.name}?`, {
      title: 'Send Password Reset',
      confirmText: 'Send',
      cancelText: 'Cancel',
      isDestructive: false
    });
    if (!confirmed) return;
    try {
      await customerMutations.resetPassword(customer.email);
      showAlertModal('Reset email dispatched.');
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to send reset email.'));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-full overflow-hidden">
      <div className="p-6 border-b flex justify-between items-start bg-slate-50">
        <div>
          <button
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-800 mb-2 flex items-center gap-1"
          >
            ← Back to List
          </button>
          <div className="flex items-center gap-3">
            <h3 className="text-2xl font-bold text-slate-900">{customer.name}</h3>
            <div className="flex gap-1">
              {canEdit && (
                <button
                  onClick={onEdit}
                  className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors border border-transparent hover:border-teal-100"
                  title="Edit Record"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={handleResetPassword}
                className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-transparent hover:border-orange-100"
                title="Send Password Reset"
              >
                <Key className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600 mt-1">
            <span className="flex items-center gap-1">
              <Mail className="w-4 h-4 text-teal-600" /> {customer.email}
            </span>
            <span className="flex items-center gap-1">
              <Phone className="w-4 h-4 text-teal-600" /> {customer.phone}
            </span>
            {customer.dob && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4 text-teal-600" />
                DOB: {formatDobForDisplay(customer.dob || '')}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-2 uppercase font-black tracking-widest">
            System ID: {customer.id}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg border text-center min-w-[100px] shadow-sm">
          <div className="text-2xl font-bold text-teal-600">{customer.visitCount ?? 0}</div>
          <div className="text-xs text-slate-500 uppercase font-bold">Bookings</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <div className="xl:col-span-1">
            <CustomerHistoryPanel
              customerId={customer.id}
              systemConfig={systemConfig}
              selectedHistoryId={selectedHistoryId}
              onSelectHistory={setSelectedHistoryId}
              onAppointmentResolved={setSelectedAppointment}
              onOpenReview={openReview}
            />
          </div>
          <div className="xl:col-span-1 scroll-mt-4" ref={reviewPanelRef}>
            <CustomerMedicalReview
              appointment={selectedAppointment}
              services={services}
              questionnaires={questionnaires}
              systemConfig={systemConfig}
              canRevokeCertificate={canRevokeCertificate}
              canOverrideLockedPaperwork={canOverrideLockedPaperwork}
              isHighlighted={isReviewHighlighted}
            />
          </div>
          <div className="xl:col-span-1">
            <CustomerDispatchLogs customerId={customer.id} />
          </div>
        </div>
      </div>
    </div>
  );
};
